import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  Alert,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Edit,
  Close,
  ExpandMore,
  ExpandLess,
  Comment,
  Send,
  Description,
  Delete,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

const MilestoneReportApproval = ({ 
  open, 
  onClose, 
  reportId,
  onApproved,
  onChangesRequested,
  onRejected,
  onDeleted,
  userRole = 'client' // 'client' or 'contractor'
}) => {
  const { getApiClient } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [changeRequest, setChangeRequest] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    scope: true,
    deliverables: true,
    exclusions: true,
    acceptance: true,
  });

  useEffect(() => {
    if (open && reportId) {
      fetchReport();
    }
  }, [open, reportId]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`/milestone-reports/${reportId}`);
      setReport(response.data.data);
      setComments(response.data.data.comments || []);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to load stage report');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const apiClient = getApiClient();
      await apiClient.post(`/milestone-reports/${reportId}/comments`, {
        commentText: newComment,
        section: 'general',
      });
      
      setNewComment('');
      fetchReport(); // Refresh to get new comment
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this stage report and release the approved payment amount?')) {
      return;
    }

    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/milestone-reports/${reportId}/approve`, {
        approvalNotes,
      });
      
      alert('Stage report approved. The approved amount can now move through the payment-release flow.');
      
      if (onApproved) {
        onApproved(report);
      }
      
      onClose();
    } catch (error) {
      console.error('Error approving report:', error);
      alert('Failed to approve report');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!changeRequest.trim()) {
      alert('Please describe what changes you need');
      return;
    }

    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/milestone-reports/${reportId}/request-changes`, {
        changeRequest,
      });
      
      alert('✅ Change request sent to contractor');
      
      if (onChangesRequested) {
        onChangesRequested(report);
      }
      
      onClose();
    } catch (error) {
      console.error('Error requesting changes:', error);
      alert('Failed to request changes');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this stage report? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      const apiClient = getApiClient();
      await apiClient.post(`/milestone-reports/${reportId}/reject`, {
        rejectionReason,
      });
      
      alert('Stage report rejected');
      
      if (onRejected) {
        onRejected(report);
      }
      
      onClose();
    } catch (error) {
      console.error('Error rejecting report:', error);
      alert('Failed to reject report');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Safe JSON parse helper
  const safeJsonParse = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return [];
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      under_review: 'warning',
      approved: 'success',
      changes_requested: 'warning',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const [deleting, setDeleting] = useState(false);
  const handleDeleteDraft = async () => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await getApiClient().delete(`/milestone-reports/${reportId}`);
      if (onDeleted) onDeleted(reportId);
      onClose();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Could not delete the draft.');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      submitted: 'Submitted',
      under_review: 'Under Review',
      approved: 'Approved',
      changes_requested: 'Changes Requested',
      rejected: 'Rejected',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={onClose} />
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!report) {
    return null;
  }

  const isClient = userRole === 'client';
  const canTakeAction = isClient && ['submitted', 'under_review'].includes(report.status);
  const isReadOnly = !canTakeAction;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '90vh',
        },
      }}
    >
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Stage Completion Report</Typography>
            <Typography variant="caption" color="text.secondary">
              {report.report_number}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={getStatusLabel(report.status)}
              color={getStatusColor(report.status)}
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Branded Header - Matching Inspection Report */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            background: brand.gradient,
            color: 'white',
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              textAlign: 'center',
              letterSpacing: 2,
            }}
          >
            STAGE COMPLETION REPORT
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Project
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {report.project_name || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Work Stage
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {report.milestone_name}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Report Date
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {new Date(report.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Report ID
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {report.report_number || report.id}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Contractor
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {report.contractor_name || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Status
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {getStatusLabel(report.status)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>

            {/* Overview */}
            <Paper elevation={0} sx={{ p: 4, mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: brand.primary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `3px solid ${brand.primary}`,
                }}
              >
                Stage Overview
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {report.milestone_overview || 'Not provided'}
              </Typography>
            </Paper>

            {/* Scope of Work */}
            <Paper elevation={0} sx={{ p: 4, mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: brand.primary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `3px solid ${brand.primary}`,
                }}
              >
                Scope of Work
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {report.scope_of_work || 'Not provided'}
              </Typography>
            </Paper>

            {/* Deliverables */}
            <Paper elevation={0} sx={{ p: 4, mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: brand.primary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `3px solid ${brand.primary}`,
                }}
              >
                Deliverables ({report.deliverables?.length || 0})
              </Typography>
              <List>
                {report.deliverables?.map((deliverable, index) => (
                  <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {index + 1}. {deliverable.deliverable_name}
                      </Typography>
                      <Chip label={deliverable.deliverable_type} size="small" variant="outlined" />
                      {deliverable.ai_suggested && (
                        <Chip label="AI" size="small" color="secondary" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {deliverable.description}
                    </Typography>
                    {deliverable.acceptance_criteria && (
                      <Typography variant="caption" color="text.secondary">
                        ✓ Acceptance: {deliverable.acceptance_criteria}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Exclusions */}
            {report.exclusions && safeJsonParse(report.exclusions).length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box 
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => toggleSection('exclusions')}
                >
                  <Typography variant="h6">Exclusions</Typography>
                  <IconButton size="small">
                    {expandedSections.exclusions ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={expandedSections.exclusions}>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    {safeJsonParse(report.exclusions).map((exclusion, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={`• ${exclusion}`} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Paper>
            )}

            {/* Acceptance Criteria */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box 
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => toggleSection('acceptance')}
              >
                <Typography variant="h6">Acceptance Criteria</Typography>
                <IconButton size="small">
                  {expandedSections.acceptance ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.acceptance}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {report.acceptance_criteria || 'Not provided'}
                </Typography>
              </Collapse>
            </Paper>

            {/* Dependencies */}
            {report.dependencies && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Dependencies</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {report.dependencies}
                </Typography>
              </Paper>
            )}

            {/* Reference Photos */}
            {report.reference_photos && safeJsonParse(report.reference_photos).length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Reference Photos</Typography>
                <Grid container spacing={2}>
                  {safeJsonParse(report.reference_photos).map((photo, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="150"
                          image={photo.url}
                          alt={photo.caption || `Photo ${index + 1}`}
                        />
                        {photo.caption && (
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption">{photo.caption}</Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* Completion documents: plans, 3D designs, certificates… */}
            {report.reference_documents && safeJsonParse(report.reference_documents).length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Completion Documents</Typography>
                <List dense disablePadding>
                  {safeJsonParse(report.reference_documents).map((doc, index) => (
                    <ListItem
                      key={`${doc.name || 'document'}-${index}`}
                      component="a"
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ px: 0, color: 'primary.main' }}
                    >
                      <ListItemText
                        primary={doc.name || `Document ${index + 1}`}
                        secondary={doc.note || doc.type || 'Open or download'}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Timeline */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Timeline</Typography>
              <Typography variant="body2">
                {report.estimated_duration ? 
                  `${report.estimated_duration} ${report.duration_unit}` : 
                  'Not specified'}
              </Typography>
            </Paper>

            {/* Status History */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Status</Typography>
              <List dense>
                {report.created_at && (
                  <ListItem>
                    <ListItemText 
                      primary="Created" 
                      secondary={new Date(report.created_at).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                {report.submitted_at && (
                  <ListItem>
                    <ListItemText 
                      primary="Submitted" 
                      secondary={new Date(report.submitted_at).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                {report.approved_at && (
                  <ListItem>
                    <ListItemText 
                      primary="Approved" 
                      secondary={new Date(report.approved_at).toLocaleDateString()}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            {/* Comments */}
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Comments ({comments.length})</Typography>
                <IconButton size="small" onClick={() => setShowComments(!showComments)}>
                  {showComments ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={showComments}>
                {comments.length > 0 && (
                  <List dense sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                    {comments.map((comment, index) => (
                      <ListItem key={index} alignItems="flex-start">
                        <ListItemText
                          primary={comment.comment_text}
                          secondary={new Date(comment.created_at).toLocaleDateString()}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    color="primary"
                  >
                    <Send />
                  </IconButton>
                </Box>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>

        {/* Action Sections (for client) */}
        {canTakeAction && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Review this stage report carefully. You can approve the stage and release payment, request changes, or reject it.
            </Alert>

            {/* Approval Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom color="success.main">
                Approve Stage / Release Payment
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Optional approval notes..."
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="success"
                startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}
                onClick={handleApprove}
                disabled={actionLoading}
              >
                Approve Stage & Release Payment
              </Button>
            </Paper>

            {/* Request Changes Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom color="warning.main">
                Request Changes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Describe what changes you need..."
                value={changeRequest}
                onChange={(e) => setChangeRequest(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={actionLoading ? <CircularProgress size={20} /> : <Edit />}
                onClick={handleRequestChanges}
                disabled={actionLoading || !changeRequest.trim()}
              >
                Request Changes
              </Button>
            </Paper>

            {/* Reject Section */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom color="error.main">
                Reject Report
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="error"
                startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
                onClick={handleReject}
                disabled={actionLoading || !rejectionReason.trim()}
              >
                Reject Report
              </Button>
            </Paper>
          </Box>
        )}

        {/* Read-only message */}
        {isReadOnly && report.status === 'draft' && !isClient && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This is an unsent draft. The owner has not seen it. To send a report, open
            <strong> Submit completion report</strong> on the stage and submit it there. You can delete this draft.
          </Alert>
        )}
        {isReadOnly && !(report.status === 'draft' && !isClient) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This report is {getStatusLabel(report.status).toLowerCase()} and cannot be modified.
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions>
        {report.status === 'draft' && !isClient && (
          <Button
            color="error"
            startIcon={deleting ? <CircularProgress size={18} /> : <Delete />}
            disabled={deleting}
            onClick={handleDeleteDraft}
            sx={{ mr: 'auto' }}
          >
            Delete draft
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MilestoneReportApproval;
